import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Spinner, Badge } from "react-bootstrap";
import usePatientClinicalMemory from "../../hooks/usePatientClinicalMemory";
import HorizontalTimeline from "./HorizontalTimeline";
import StaleBanner from "./StaleBanner";
import AppointmentStatusCounts from "./AppointmentStatusCounts";

/**
 * ClinicalHistoryFullPanel: body-only full Clinical History view.
 * No Modal wrapper. Used both inside the standalone PatientClinicalHistoryModal
 * AND embedded inline inside EditAppointments.jsx.
 */
function ListBlock({ title, items, emptyMessage }) {
  return (
    <section className="mb-3">
      <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">{title}</h6>
      {items && items.length > 0 ? (
        <ul className="ps-3 mb-0">
          {items.map((it, i) => (
            <li key={i} className="mb-1">{it}</li>
          ))}
        </ul>
      ) : (
        <div className="text-muted small fst-italic">{emptyMessage || "Nothing recorded yet."}</div>
      )}
    </section>
  );
}

function ICD10Block({ candidates }) {
  return (
    <section className="mb-3">
      <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">
        Suggested ICD10 Candidates
      </h6>
      <div className="alert alert-primary py-2 px-3 small mb-2 d-flex align-items-center">
        <i className="fa-light fa-sparkles text-primary me-2" />
        ICD10 suggestions are for clinician review only. They are not automatically applied.
      </div>
      {candidates && candidates.length > 0 ? (
        <div className="d-flex flex-column gap-2">
          {candidates.map((c, i) => (
            <div key={i} className="border rounded p-2">
              <div className="d-flex align-items-start gap-2">
                <Badge bg="secondary" className="text-uppercase">{c.code}</Badge>
                <div className="flex-grow-1">
                  <div className="fw-semibold">{c.description}</div>
                  <div className="small text-muted mb-1">{c.reason}</div>
                  <Badge
                    bg={c.confidence === "high" ? "success" : c.confidence === "medium" ? "warning" : "light"}
                    text={c.confidence === "low" ? "dark" : undefined}
                  >
                    Confidence: {c.confidence}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted small fst-italic">No ICD10 candidates suggested.</div>
      )}
    </section>
  );
}

export default function ClinicalHistoryFullPanel({
  active = true,
  patientId,
  practiceId,
  patientName,
  onBack,
  onClose,
  showHeader = true,
}) {
  const { memory, loading, error, isStale, pollingGaveUp, progress, refresh, checkAgain } =
    usePatientClinicalMemory(active ? patientId : null, practiceId);

  const timeline = useMemo(() => memory?.timeline || [], [memory]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  useEffect(() => {
    if (!timeline.length) {
      setSelectedSessionId(null);
      return;
    }
    const newest = [...timeline].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    })[0];
    setSelectedSessionId((cur) =>
      cur && timeline.find((t) => t.sessionId === cur) ? cur : newest?.sessionId,
    );
  }, [timeline]);

  const selectedEntry = timeline.find((t) => t.sessionId === selectedSessionId);
  const status = memory?.indexStatus;
  const isInitialBuild = status === "NOT_INDEXED" || status === "INDEXING";
  const fullHistory = memory?.fullHistory || {};

  return (
    <div>
      {showHeader && (
        <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
          {/* Left: Back (or empty spacer to keep title centred) */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            {onBack && (
              <Button variant="outline-secondary" size="sm" onClick={onBack} title="Back">
                <i className="fa-solid fa-arrow-left me-1" /> Back
              </Button>
            )}
          </div>
          {/* Centre: title */}
          <h5 className="mb-0 text-center" style={{ flex: "0 1 auto" }}>
            <i className="fa-solid fa-clock-rotate-left me-2 text-primary" />
            Clinical History{patientName ? ":" : ""}
            {patientName ? (
              <span className="text-muted small ms-1 fw-normal">{patientName}</span>
            ) : null}
          </h5>
          {/* Right: spacer matching left so title stays centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {(isStale || status === "INDEXING") && (
        <StaleBanner
          status={status}
          gaveUp={pollingGaveUp}
          progress={progress}
          onRetry={checkAgain}
        />
      )}

      {error && (
        <div className="alert alert-danger py-2 px-3 small mb-3">
          <i className="fa-solid fa-triangle-exclamation me-2" />
          {error}
        </div>
      )}

      {memory?.lastError && status === "FAILED" && (
        <div className="alert alert-danger py-2 px-3 small mb-3">
          <i className="fa-solid fa-circle-xmark me-2" />
          Last build failed: {memory.lastError.message}. Press Refresh to try again.
        </div>
      )}

      {loading && !memory ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading patient history…
        </div>
      ) : isInitialBuild && (!timeline.length) ? (
        <div className="text-center py-4">
          <p className="text-muted mb-3">
            {status === "INDEXING"
              ? "Building this patient's clinical history for the first time. This may take up to a minute."
              : "No clinical history has been built yet for this patient."}
          </p>
          {status !== "INDEXING" && (
            <Button variant="primary" onClick={refresh}>
              <i className="fa-solid fa-rotate me-2" /> Build History
            </Button>
          )}
        </div>
      ) : (
        <>
          <AppointmentStatusCounts patientId={patientId} practiceId={practiceId} variant="inline" />

          <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">Session Timeline</h6>
          <HorizontalTimeline
            practiceId={practiceId}
            timeline={timeline}
            selectedSessionId={selectedSessionId}
            onSelect={(entry) => setSelectedSessionId(entry.sessionId)}
          />

          {selectedEntry && (
            <div className="border-start border-3 border-primary ps-3 my-3 bg-light rounded-end py-2">
              <div className="text-muted small mb-1">
                <strong>Selected session:</strong>{" "}
                {selectedEntry.date
                  ? new Date(selectedEntry.date).toLocaleDateString("en-GB", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "Date unknown"}
                {" · "}
                {selectedEntry.status || "Session"}
                {selectedEntry.sourceType === "SYNCTO_NOTE" ? " · SyncTo" : ""}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{selectedEntry.summary}</div>
            </div>
          )}

          {/* Full-width divider between the session detail and the 3-column body */}
          <hr className="my-4" />

          <Row className="g-0">
            {/* Column 1: History Summary + Treatment Themes */}
            <Col md={4} className="pe-md-3 border-end">
              <section className="mb-3">
                <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">History Summary</h6>
                <p style={{ whiteSpace: "pre-wrap" }}>
                  {fullHistory.historySummaryParagraph || "No summary available yet."}
                </p>
              </section>
              <ListBlock
                title="Treatment Themes"
                items={fullHistory.treatmentThemes}
                emptyMessage="No recurring themes identified yet."
              />
            </Col>

            {/* Column 2: Suggested Focus for Today + Suggested Questions */}
            <Col md={4} className="px-md-3 border-end">
              <section className="mb-3">
                <h6 className="text-uppercase small fw-semibold mb-2 px-3 py-2 rounded table-primary">
                  Suggested Focus for Today
                </h6>
                <div className="alert alert-primary py-2 px-3 small mb-2 d-flex align-items-center">
                  <i className="fa-light fa-sparkles text-primary me-2" />
                  AI-suggested. Practitioner must review.
                </div>
                {fullHistory.suggestedFocusForToday?.length ? (
                  <ul className="ps-3 mb-0">
                    {fullHistory.suggestedFocusForToday.map((it, i) => (
                      <li key={i} className="mb-1">{it}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted small fst-italic">No suggested focus yet.</div>
                )}
              </section>
              <ListBlock
                title="Suggested Questions"
                items={fullHistory.suggestedQuestions}
                emptyMessage="No suggested questions yet."
              />
            </Col>

            {/* Column 3: ICD10 Candidates + Missing Information */}
            <Col md={4} className="ps-md-3">
              <ICD10Block candidates={fullHistory.suggestedICD10Candidates} />
              <ListBlock
                title="Missing Information"
                items={fullHistory.missingInformation}
                emptyMessage="No documentation gaps flagged."
              />
            </Col>
          </Row>

          {/* No model name here: which AI deployment produced the history is
              an internal detail and must not appear anywhere in the UI. */}
          {memory?.aiMeta?.generatedAt && (
            <div className="text-muted small mt-3 pt-2 border-top">
              Generated {new Date(memory.aiMeta.generatedAt).toLocaleString("en-GB")}
            </div>
          )}
        </>
      )}

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refresh}
          disabled={loading || status === "INDEXING"}
        >
          <i className="fa-solid fa-rotate me-1" /> Refresh
        </Button>
        {onClose && (
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
        )}
      </div>
    </div>
  );
}
